import { usePortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * The authenticator an organization requires, met on the invitation page itself (plan-014 review I-18).
 *
 * An invitee to an organization whose sign-in policy requires an authenticator was answered `401 assurance_required`
 * on Accept, and could do nothing about it: My security's Confirm said "Choose an organization first" and the
 * challenge needs an authenticator the person does not have yet. Leaving the page would also lose the invitation (its
 * token is held in memory only — review I-15). So the step is taken here:
 *
 *   - a person WITH an authenticator enters its code (or a recovery code) — the sign-in's second step;
 *   - a person WITHOUT one confirms their password (the API allows exactly this step-up with no organization: a first
 *     authenticator for a person in none), scans the QR code drawn in the browser, enters the first code, keeps the
 *     recovery codes — and the enrolment itself counts as this sign-in's second step.
 *
 * Then `onDone` accepts again. Nothing here accepts by itself.
 *
 * AMENDED 2026-11-03 (design §16.37 amendment): a person with NO password and no Google or Microsoft — who created
 * their sign-in here with an emailed code — confirms with a code emailed to the invited address
 * (`requestSignInCode`, then `stepUpWithEmailCode`) instead of a password, then adds the authenticator as above.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Field, Input } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import "../../../api/slice/personal-security.js";
import { QrCode } from "../../personal-security/qr-code.js";
import "../../personal-security/personal-security.css";

export function AuthenticatorStep(props: { readonly accountName: string; readonly email: string; readonly onDone: () => void }): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const methods = useQuery({ queryKey: ["personal-security", "sign-in-methods", "invitation"], queryFn: () => gateway.readMySignInMethods(), retry: false });
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [enrolment, setEnrolment] = useState<{ readonly otpauthUri: string } | null>(null);
  const [recovery, setRecovery] = useState<readonly string[] | null>(null);
  const [problem, setProblem] = usePortalMessage();

  const challenge = useMutation({
    mutationFn: () => gateway.answerMfaChallenge(code.replace(/\s/g, "").length > 8 ? "recovery_code" : "totp", code.replace(/\s/g, "")),
    onSuccess: () => props.onDone(),
    onError: (raised: unknown) => setProblem(() => (apiProblemText(raised, portalText("core", "start.that_code_is_not_right_273772")))),
  });
  // 2026-11-03: the emailed code that confirms a person with no password, once asked for.
  const [codeSent, setCodeSent] = useState(false);
  const askForCode = useMutation({
    mutationFn: () => gateway.requestSignInCode(props.email),
    onSuccess: () => {
      setPassword("");
      setProblem(null);
      setCodeSent(true);
    },
    onError: (raised: unknown) => setProblem(() => (apiProblemText(raised, portalText("core", "start.your_authenticator_could_not_be_set_up_just_now_e8d80f")))),
  });
  const start = useMutation({
    mutationFn: async () => {
      if (codeSent) await gateway.stepUpWithEmailCode(password.replace(/\s/g, ""));
      else await gateway.confirmIdentity("password", password);
      return gateway.startTotpEnrolment();
    },
    onSuccess: (started) => {
      setPassword("");
      setProblem(null);
      setEnrolment({ otpauthUri: started.otpauthUri });
    },
    onError: (raised: unknown) => {
      setPassword("");
      setProblem(() => (apiProblemText(raised, portalText("core", "start.your_authenticator_could_not_be_set_up_just_now_e8d80f"))));
    },
  });
  const confirm = useMutation({
    mutationFn: () => gateway.confirmTotpEnrolment(code.replace(/\s/g, "")),
    onSuccess: (confirmed) => {
      setCode("");
      setProblem(null);
      if (confirmed.recoveryCode !== null && confirmed.recoveryCode.length > 0) setRecovery(confirmed.recoveryCode);
      else props.onDone();
    },
    onError: (raised: unknown) => setProblem(() => (apiProblemText(raised, portalText("core", "auth.that_code_is_not_right_enter_the_6_digits_your_app_shows_now_5af97e")))),
  });

  const said = problem === null ? null : (
    <p className="c-note c-note-critical" role="alert">
      {problem}
    </p>
  );

  if (methods.isPending) return <p className="c-note">{portalText("core", "start.checking_your_sign_in_f4f55d")}</p>;
  if (methods.isError) return <p className="c-note c-note-critical">{apiProblemText(methods.error, portalText("core", "start.your_sign_in_could_not_be_read_ad05f1"))}</p>;

  if (methods.data.totp.state === "active") {
    return (
      <section className="op-block" aria-label={portalText("core", "start.enter_your_authenticator_code_4726cf")}>
        <p className="c-note">{portalText("core", "start._asks_for_the_code_from_your_authenticator_app_before_you_join_ba0585", { value0: props.accountName })}</p>
        <Field label={portalText("core", "start.authenticator_code_2908b4")} description={portalText("core", "start.the_6_digits_your_app_shows_now_or_one_of_your_recovery_codes_ac60dd")}>
          {(control) => <Input {...control} value={code} inputMode="numeric" autoComplete="one-time-code" onChange={(event) => setCode(event.target.value)} />}
        </Field>
        {said}
        <Button variant="primary" disabled={code.trim() === "" || challenge.isPending} onClick={() => challenge.mutate()}>
          {challenge.isPending ? portalText("core", "auth.checking_820d60") : portalText("core", "auth.continue_2e0262")}
        </Button>
      </section>
    );
  }

  if (recovery !== null) {
    return (
      <section className="op-block" aria-label={portalText("core", "start.your_recovery_codes_781e39")}>
        <p className="c-note">{portalText("core", "start.your_authenticator_is_on_keep_these_recovery_codes_somewhere_safe_4a8f07")}</p>
        <ol className="ps-codes" aria-label={portalText("core", "start.recovery_codes_b3f5f8")}>
          {recovery.map((one) => (
            <li key={one}>
              <code>{one}</code>
            </li>
          ))}
        </ol>
        <Button variant="primary" onClick={() => props.onDone()}>{portalText("core", "start.i_kept_them_join_78ffa7")}{" "}{props.accountName}
        </Button>
      </section>
    );
  }

  if (enrolment !== null) {
    return (
      <section className="op-block" aria-label={portalText("core", "start.add_an_authenticator_01e92f")}>
        <p className="c-note">{portalText("core", "start.scan_this_code_with_your_authenticator_app_then_enter_the_6_digit_1c230e")}</p>
        <QrCode text={enrolment.otpauthUri} label={portalText("core", "start.qr_code_for_your_authenticator_app_1f4d21")} />
        <Field label={portalText("core", "start.code_from_your_app_2e0896")}>
          {(control) => <Input {...control} value={code} inputMode="numeric" autoComplete="one-time-code" onChange={(event) => setCode(event.target.value)} />}
        </Field>
        {said}
        <Button variant="primary" disabled={code.trim() === "" || confirm.isPending} onClick={() => confirm.mutate()}>
          {confirm.isPending ? portalText("core", "auth.checking_820d60") : portalText("core", "start.turn_it_on_cc4d3c")}
        </Button>
      </section>
    );
  }

  const byEmailCode = !methods.data.password.enabled && !methods.data.google.linked && !methods.data.microsoft.linked;
  if (byEmailCode) {
    return (
      <section className="op-block" aria-label={portalText("core", "start.add_an_authenticator_01e92f")}>
        <p className="c-note">{portalText("core", "start.code.confirm_by_email_lede", { value0: props.accountName, value1: props.email })}</p>
        {codeSent ? (
          <Field label={portalText("core", "auth.sign_in_code_6af151")}>
            {(control) => <Input {...control} value={password} inputMode="numeric" autoComplete="one-time-code" onChange={(event) => setPassword(event.target.value)} />}
          </Field>
        ) : null}
        {said}
        {codeSent ? (
          <Button variant="primary" disabled={password.trim() === "" || start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? portalText("core", "start.confirming_0c2708") : portalText("core", "start.add_an_authenticator_01e92f")}
          </Button>
        ) : (
          <Button variant="primary" disabled={askForCode.isPending} onClick={() => askForCode.mutate()}>
            {portalText("core", "personal_security.email_me_a_code")}
          </Button>
        )}
      </section>
    );
  }

  return (
    <section className="op-block" aria-label={portalText("core", "start.add_an_authenticator_01e92f")}>
      <p className="c-note">{portalText("core", "start._asks_everyone_to_use_an_authenticator_app_confirm_your_password_171ce5", { value0: props.accountName })}</p>
      <Field label={portalText("core", "start.your_password_26d745")}>
        {(control) => <Input {...control} type="password" value={password} autoComplete="current-password" onChange={(event) => setPassword(event.target.value)} />}
      </Field>
      {said}
      <Button variant="primary" disabled={password === "" || start.isPending} onClick={() => start.mutate()}>
        {start.isPending ? portalText("core", "start.confirming_0c2708") : portalText("core", "start.add_an_authenticator_01e92f")}
      </Button>
    </section>
  );
}
